import React, { useState, useEffect, useMemo, useContext } from 'react'
import { FilterMatchMode, FilterOperator } from 'primereact/api'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Dialog } from 'primereact/dialog'
import { Button } from 'primereact/button'
import { InputNumber } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'
import { Tooltip } from 'primereact/tooltip'
import { Row, Col, Card, CardBody } from 'reactstrap'
import MainContentLayout from '../../Layout/MainContentLayout'
import DashboardCards from '../../Dashboard/DashboardCards'
import { formatCurrency, formatDate } from '../../shared/utils'
import { DateFilterTemplate } from '../../shared/FilterTemplates'
import { printPaymentBreakdown } from './printPaymentBreakdown'
import server from '../../../server'
import servicesIcon1 from '../../../assets/images/services-icon/01.png'
import servicesIcon2 from '../../../assets/images/services-icon/02.png'
import servicesIcon3 from '../../../assets/images/services-icon/03.png'
import servicesIcon4 from '../../../assets/images/services-icon/04.png'
import { AuthenticationContext } from '../../../Auth/authentication.context'
import useTableRowsPerPage from '../../../hooks/useTableRowsPerPage'
import { getInitialPaginationState } from '../../../shared/tablePagination'

const initialPendingFilters = {
  supplierInvoiceNo: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
  manufacturerNamesDisplay: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
  createdAt: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.BETWEEN }] },
  confirmedExtendTotal: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.BETWEEN }] },
  confirmedPaValue: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.BETWEEN }] },
}
const initialHistoryFilters = {
  paidAt: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.BETWEEN }] },
  transactionId: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
  totalPaAmount: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.BETWEEN }] },
}

function displayPaymentTransactionId(value) {
  const s = value != null ? String(value).trim() : ''
  if (!s || s === '-') return 'n/a'
  return s
}

export default function PharmacyPaPayments() {
  const { user } = useContext(AuthenticationContext)
  const { defaultRows, rowsPerPageOptions, applyPageEvent } = useTableRowsPerPage()
  const [pagination, setPagination] = useState(() =>
    getInitialPaginationState(user)
  )
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [breakdownVisible, setBreakdownVisible] = useState(false)
  const [breakdown, setBreakdown] = useState(null)
  const [breakdownLoading, setBreakdownLoading] = useState(false)
  const [pendingFilters, setPendingFilters] = useState(initialPendingFilters)
  const [historyFilters, setHistoryFilters] = useState(initialHistoryFilters)

  useEffect(() => {
    setPagination((prev) => ({ ...prev, rows: defaultRows }))
  }, [defaultRows])

  const rawPayables = data?.pending?.payables ?? []
  const payablesWithDateObjects = useMemo(
    () => rawPayables.map((p) => ({ ...p, createdAt: p.createdAt ? new Date(p.createdAt) : null })),
    [rawPayables]
  )
  const rawHistory = data?.paymentHistory ?? []
  const historyWithDateObjects = useMemo(
    () => rawHistory.map((r) => ({ ...r, paidAt: r.paidAt ? new Date(r.paidAt) : null })),
    [rawHistory]
  )

  const filteredPendingPayables = useMemo(() => {
    if (!rawPayables.length) return []
    const f = pendingFilters
    return rawPayables.filter((row) => {
      if (f.supplierInvoiceNo?.constraints?.[0]?.value != null && String(f.supplierInvoiceNo.constraints[0].value).trim() !== '') {
        const v = (row.supplierInvoiceNo ?? '').toString().toLowerCase()
        const q = String(f.supplierInvoiceNo.constraints[0].value).toLowerCase()
        if (!v.includes(q)) return false
      }
      if (f.manufacturerNamesDisplay?.constraints?.[0]?.value != null && String(f.manufacturerNamesDisplay.constraints[0].value).trim() !== '') {
        const v = (row.manufacturerNamesDisplay ?? '').toString().toLowerCase()
        const q = String(f.manufacturerNamesDisplay.constraints[0].value).toLowerCase()
        if (!v.includes(q)) return false
      }
      const confExt = f.confirmedExtendTotal?.constraints?.[0]?.value
      if (Array.isArray(confExt) && (confExt[0] != null || confExt[1] != null)) {
        const val = Number(row.confirmedExtendTotal)
        if (confExt[0] != null && val < Number(confExt[0])) return false
        if (confExt[1] != null && val > Number(confExt[1])) return false
      }
      const confPa = f.confirmedPaValue?.constraints?.[0]?.value
      if (Array.isArray(confPa) && (confPa[0] != null || confPa[1] != null)) {
        const val = Number(row.confirmedPaValue)
        if (confPa[0] != null && val < Number(confPa[0])) return false
        if (confPa[1] != null && val > Number(confPa[1])) return false
      }
      const dateRange = f.createdAt?.constraints?.[0]?.value
      if (Array.isArray(dateRange) && (dateRange[0] != null || dateRange[1] != null)) {
        const rowDate = new Date(row.createdAt)
        if (dateRange[0] != null) {
          const start = new Date(dateRange[0])
          start.setHours(0, 0, 0, 0)
          if (rowDate < start) return false
        }
        if (dateRange[1] != null) {
          const end = new Date(dateRange[1])
          end.setHours(23, 59, 59, 999)
          if (rowDate > end) return false
        }
      }
      return true
    })
  }, [rawPayables, pendingFilters])

  const pendingTotals = useMemo(() => {
    const totalConfirmed = filteredPendingPayables.reduce((s, p) => s + (Number(p.confirmedExtendTotal) || 0), 0)
    const totalPa = filteredPendingPayables.reduce((s, p) => s + (Number(p.confirmedPaValue) || 0), 0)
    return { totalConfirmed, totalPa }
  }, [filteredPendingPayables])

  const onPendingFilter = (e) => setPendingFilters(e.filters)
  const onHistoryFilter = (e) => setHistoryFilters(e.filters)

  const amountBetweenFilterElement = (options) => {
    const val = options.value
    const arr = Array.isArray(val) ? val : [null, null]
    return (
      <div className='d-flex flex-column gap-2'>
        <InputNumber
          placeholder='Min'
          value={arr[0]}
          onValueChange={(e) => options.filterCallback([e.value ?? null, arr[1]], options.index)}
          className='p-column-filter w-full'
          mode='decimal'
          minFractionDigits={0}
          maxFractionDigits={2}
        />
        <InputNumber
          placeholder='Max'
          value={arr[1]}
          onValueChange={(e) => options.filterCallback([arr[0], e.value ?? null], options.index)}
          className='p-column-filter w-full'
          mode='decimal'
          minFractionDigits={0}
          maxFractionDigits={2}
        />
      </div>
    )
  }

  const manufacturerFilterElement = (options) => {
    const constraint = options.constraints?.[options.index ?? 0]
    const value = (constraint?.value ?? options.value ?? '') || ''
    return (
      <InputText
        value={value}
        onChange={(e) => options.filterCallback(e.target.value, options.index ?? 0)}
        placeholder='Manufacturer'
        className='p-column-filter w-full'
      />
    )
  }

  const dateRangeFilterElement = (options) => (
    <DateFilterTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e.value, options.index)}
    />
  )

  useEffect(() => {
    server
      .get('/users/pa-payments')
      .then((response) => {
        setData(response.data.data)
        setLoading(false)
      })
      .catch((err) => {
        setError(err.response?.data?.message || 'Failed to load Pay Statements')
        setLoading(false)
      })
  }, [])

  if (loading) {
    return (
      <MainContentLayout title='Pay Statements'>
        <p>Loading...</p>
      </MainContentLayout>
    )
  }

  if (error) {
    return (
      <MainContentLayout title='Pay Statements'>
        <div className='p-4 rounded border border-warning bg-light'>
          <p className='text-warning mb-0'>{error}</p>
          <small className='text-muted'>Pay Statements are only available for pharmacies that are part of a group.</small>
        </div>
      </MainContentLayout>
    )
  }

  const { pharmacy, pending } = data

  const openBreakdown = (batchId) => {
    setBreakdown(null)
    setBreakdownVisible(true)
    setBreakdownLoading(true)
    server
      .get(`/users/pa-payments/${batchId}`)
      .then((res) => {
        setBreakdown(res.data.data)
        setBreakdownLoading(false)
      })
      .catch(() => setBreakdownLoading(false))
  }

  const metricTitles = ['Order total', 'Total PA', 'Pending PA', 'Paid PA']
  const metricIcons = [servicesIcon1, servicesIcon2, servicesIcon3, servicesIcon4]
  const metricValues = [
    pending.totalOrderAmount,
    pending.totalPaAmount,
    pending.pendingPaAmount,
    pending.paidPaAmount,
  ]

  return (
    <MainContentLayout title='Pay Statements'>
      <Tooltip target='.p-sortable-column-icon' content='Sort' />
      <Tooltip target='.p-column-filter-menu-button' content='Filter' />
      {pharmacy?.groupName && (
        <p className='text-muted mb-3'>Group: {pharmacy.groupName}</p>
      )}

      <Row className='mb-4'>
        {metricTitles.map((title, index) => (
          <Col key={index} xl={3} md={6}>
            <Card className='mini-stat bg-primary text-white'>
              <CardBody style={{ height: '120px' }}>
                <DashboardCards
                  icon={metricIcons[index]}
                  title={title}
                  value={metricValues[index]}
                />
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Pending section */}
      <section className='mb-4'>
        <h5 className='mb-3'>Pending</h5>
        <DataTable
          value={payablesWithDateObjects}
          dataKey='id'
          showGridlines
          filters={pendingFilters}
          onFilter={onPendingFilter}
          filterDisplay='menu'
          removableSort
          emptyMessage='No pending orders. All confirmed orders have been paid.'
          tableStyle={{ minWidth: '50rem' }}
        >
          <Column
            field='supplierInvoiceNo'
            header='Order No.'
            sortable
            filter
            filterPlaceholder='Order No.'
            filterMatchMode='contains'
            showFilterOperator={false}
            showFilterMenuOptions={false}
            footer={filteredPendingPayables.length > 0 ? 'Total' : ''}
            footerStyle={{ fontWeight: 600 }}
          />
          <Column
            field='manufacturerNamesDisplay'
            header='Manufacturer'
            body={(row) => (row.manufacturerNames && row.manufacturerNames.length ? row.manufacturerNames.join(', ') : '-')}
            sortable
            sortField='manufacturerNamesDisplay'
            filter
            filterElement={manufacturerFilterElement}
            filterMatchMode='contains'
            showFilterOperator={false}
            showFilterMenuOptions={false}
            footer=""
            footerStyle={{ fontWeight: 600 }}
          />
          <Column
            field='createdAt'
            header='Confirmed date'
            body={(row) => (row.createdAt ? formatDate(row.createdAt) : '-')}
            sortable
            filter
            filterElement={dateRangeFilterElement}
            filterMatchMode='between'
            showFilterOperator={false}
            showFilterMenuOptions={false}
            footer=""
            footerStyle={{ fontWeight: 600 }}
          />
          <Column
            field='confirmedExtendTotal'
            header='Confirmed value'
            body={(row) => formatCurrency(row.confirmedExtendTotal)}
            sortable
            filter
            filterElement={amountBetweenFilterElement}
            filterMatchMode='between'
            showFilterOperator={false}
            showFilterMenuOptions={false}
            footer={filteredPendingPayables.length > 0 ? formatCurrency(pendingTotals.totalConfirmed) : ''}
            footerStyle={{ fontWeight: 600 }}
          />
          <Column
            field='confirmedPaValue'
            header='PA amount'
            body={(row) => formatCurrency(row.confirmedPaValue)}
            sortable
            filter
            filterElement={amountBetweenFilterElement}
            filterMatchMode='between'
            showFilterOperator={false}
            showFilterMenuOptions={false}
            footer={filteredPendingPayables.length > 0 ? formatCurrency(pendingTotals.totalPa) : ''}
            footerStyle={{ fontWeight: 600 }}
          />
        </DataTable>
      </section>

      {/* Payment history */}
      <section>
        <h5 className='mb-3'>Payment history</h5>
        <DataTable
          value={historyWithDateObjects}
          dataKey='id'
          showGridlines
          filters={historyFilters}
          onFilter={onHistoryFilter}
          filterDisplay='menu'
          removableSort
          emptyMessage='No payments yet.'
          selectionMode='single'
          onRowSelect={(e) => openBreakdown(e.data.id)}
          rowHover
          paginator
          rows={pagination.rows}
          first={pagination.first}
          onPage={(e) => setPagination((prev) => applyPageEvent(prev, e))}
          rowsPerPageOptions={rowsPerPageOptions}
          paginatorTemplate='FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
          currentPageReportTemplate='Showing {first} to {last} of {totalRecords} entries'
          tableStyle={{ minWidth: '50rem' }}
        >
          <Column field='id' header='Payment ID' sortable />
          <Column
            field='paidAt'
            header='Date'
            body={(row) => (row.paidAt ? formatDate(row.paidAt) : '-')}
            sortable
            filter
            filterElement={dateRangeFilterElement}
            filterMatchMode='between'
            showFilterOperator={false}
            showFilterMenuOptions={false}
          />
          <Column
            field='transactionId'
            header='Transaction / Cheque ID (optional)'
            body={(row) => {
              const d = displayPaymentTransactionId(row.transactionId)
              return d === 'n/a' ? <span className='text-muted'>n/a</span> : d
            }}
            sortable
            filter
            filterPlaceholder='Transaction / Cheque ID'
            filterMatchMode='contains'
            showFilterOperator={false}
            showFilterMenuOptions={false}
          />
          <Column
            field='totalPaAmount'
            header='Amount'
            body={(row) => formatCurrency(row.totalPaAmount)}
            sortable
            filter
            filterElement={amountBetweenFilterElement}
            filterMatchMode='between'
            showFilterOperator={false}
            showFilterMenuOptions={false}
          />
          <Column field='ordersCount' header='Orders' sortable />
        </DataTable>
      </section>

      <Dialog
        header={breakdown ? `Payment #${breakdown.paymentId} – Breakdown` : 'Payment breakdown'}
        visible={breakdownVisible}
        onHide={() => setBreakdownVisible(false)}
        style={{ width: 'min(90vw, 560px)' }}
        modal
        footer={
          breakdown && !breakdownLoading ? (
            <Button
              label='Print'
              icon='pi pi-print'
              onClick={() => printPaymentBreakdown(breakdown, { formatDate, formatCurrency, showPharmacy: true })}
            />
          ) : null
        }
      >
        {breakdownLoading && <p>Loading...</p>}
        {!breakdownLoading && breakdown && (
          <div>
            <div className='d-flex flex-wrap gap-3 mb-3'>
              <span><strong>Payment ID:</strong> {breakdown.paymentId}</span>
              <span><strong>Date:</strong> {formatDate(breakdown.paidAt)}</span>
              <span>
                <strong>Transaction / Cheque:</strong>{' '}
                {displayPaymentTransactionId(breakdown.transactionId) === 'n/a' ? (
                  <span className='text-muted'>n/a</span>
                ) : (
                  breakdown.transactionId
                )}
              </span>
              <span><strong>Total amount:</strong> {formatCurrency(breakdown.totalPaAmount)}</span>
            </div>
            <h6 className='mb-2'>Orders included</h6>
            <DataTable
              value={breakdown.orders || []}
              dataKey='id'
              size='small'
              showGridlines
            >
              <Column field='supplierInvoiceNo' header='Order No.' />
              <Column field='createdAt' header='Date' body={(r) => (r.createdAt ? formatDate(r.createdAt) : '-')} />
              <Column field='confirmedExtendTotal' header='Confirmed value' body={(r) => formatCurrency(r.confirmedExtendTotal)} />
              <Column field='confirmedPaValue' header='PA amount' body={(r) => formatCurrency(r.confirmedPaValue)} />
            </DataTable>
          </div>
        )}
      </Dialog>
    </MainContentLayout>
  )
}
